import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import Login from './components/Login'
import Loader from './components/Loader'
import AdminConsole from './components/AdminConsole'
import UserWorkspace from './components/UserWorkspace'
import { isAdmin } from './lib/user'
import { ThemeProvider } from './theme/ThemeContext'
import DesignSystemSwitcher from './components/DesignSystemSwitcher'

// Flow: login → loader (system spinning up) → role-forked shell.
// Admins get the AdminConsole (the existing dashboard); everyone else gets the
// UserWorkspace. Each shell owns its own navigation state.
// Dev-only: the design-system switcher is hidden in the client demo. Show it
// only when running the dev server AND ?ds is present in the URL.
const showDesignSwitcher =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('ds')

export default function App() {
  const [stage, setStage] = useState('login') // 'login' | 'loading' | 'dashboard'
  const [user, setUser] = useState(null) // signed-in user { user_id, name, role }

  const handleSignIn = (signedInUser) => {
    setUser(signedInUser)
    setStage('loading')
    setTimeout(() => setStage('dashboard'), 1800)
  }

  // Log out: back to login. The active shell unmounts, so its session state
  // (chat, notifications, approvals) is cleared — signing back in starts fresh.
  const handleLogout = () => {
    setUser(null)
    setStage('login')
  }

  return (
    <ThemeProvider>
      <AnimatePresence mode="wait">
        {stage === 'login' && (
          <motion.div key="login" style={{ height: '100%' }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <Login onSignIn={handleSignIn} />
          </motion.div>
        )}

        {stage === 'loading' && <Loader key="loader" />}

        {stage === 'dashboard' && (
          <motion.div
            key="dashboard"
            style={{ height: '100%' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {isAdmin(user) ? (
              <AdminConsole user={user} onLogout={handleLogout} />
            ) : (
              <UserWorkspace user={user} onLogout={handleLogout} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {showDesignSwitcher && <DesignSystemSwitcher />}
    </ThemeProvider>
  )
}
